import { uuidv7 } from '@nexus/core';
import { markReachable, markUnreachable } from './reachability';

/**
 * Error answer of the API. `code` is a stable identifier where the server sends one, `body` the
 * parsed answer with further details, e.g. the errors per field.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** ID of a server error (status 5xx), for the person to tell the support. */
  get errorId(): string | undefined {
    const id = (this.body as { errorId?: unknown } | undefined)?.errorId;
    return typeof id === 'string' ? id : undefined;
  }
}

/**
 * i18n keys per field of an `invalid-input` answer, by the path the API names (`email`,
 * `address.street`). The server sends a key only where the schema sets one
 * (docs/architecture/api.md); other issues get `fallback`. The first issue of a path wins.
 * `undefined` for every other error.
 */
export function inputErrors(error: unknown, fallback: string): Record<string, string> | undefined {
  if (!(error instanceof ApiError) || error.message !== 'invalid-input') return undefined;
  const issues = (error.body as { issues?: unknown } | undefined)?.issues;
  const errors: Record<string, string> = {};
  for (const issue of Array.isArray(issues) ? (issues as unknown[]) : []) {
    const { path, message } = (issue ?? {}) as { path?: unknown; message?: unknown };
    if (typeof path !== 'string' || path in errors) continue;
    errors[path] = typeof message === 'string' ? message : fallback;
  }
  return errors;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  body?: unknown;
  /** Further headers, e.g. the file name of an upload. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Fixed key for retries of the same write (ADR-0013); generated when missing. */
  idempotencyKey?: string;
  /**
   * Milliseconds until the request counts as failed for lack of network; `0` waits as long as
   * the browser does. Reads of JSON default to `READ_TIMEOUT_MS`, everything else to `0`.
   */
  timeout?: number;
}

/**
 * A read of JSON that got no answer within this time counts as without network. On weak mobile
 * network a request can hang for minutes without failing; the app then works from the device.
 */
export const READ_TIMEOUT_MS = 30_000;

/**
 * No answer within `RequestOptions.timeout`. A `TypeError` like every failure for lack of
 * network, so code that falls back to the device on a failed fetch does so here too.
 */
export class NetworkTimeoutError extends TypeError {
  constructor() {
    super('request-timeout');
    this.name = 'NetworkTimeoutError';
  }
}

/** `fetch` with a time limit; marks whether the server answered (`reachability.ts`). */
async function fetchWithin(url: string, init: RequestInit, timeout: number): Promise<Response> {
  const controller = timeout > 0 ? new AbortController() : null;
  const outer = init.signal;
  const forward = () => controller?.abort(outer?.reason);
  if (outer?.aborted) forward();
  outer?.addEventListener('abort', forward);
  let timedOut = false;
  const timer = controller
    ? setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeout)
    : null;
  try {
    const response = await fetch(url, controller ? { ...init, signal: controller.signal } : init);
    markReachable();
    return response;
  } catch (error) {
    if (timedOut) {
      markUnreachable();
      throw new NetworkTimeoutError();
    }
    // Without network fetch fails with a TypeError; an abort by the caller says nothing about it.
    if (error instanceof TypeError) markUnreachable();
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
    outer?.removeEventListener('abort', forward);
  }
}

const listeners = new Set<() => void>();

/** Called when the API answers 401: the session ended (logout elsewhere, expiry, revocation). */
export function onSessionEnded(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Sends a request and throws `ApiError` for every answer that is not a success. */
async function send(
  method: Method,
  path: string,
  options: RequestOptions,
  accept: string,
): Promise<Response> {
  const headers: Record<string, string> = { Accept: accept };
  if (method !== 'GET') {
    // Required by the CSRF guard of the API (packages/auth/src/guards.ts).
    headers['x-nexus-request'] = '1';
    headers['Idempotency-Key'] = options.idempotencyKey ?? uuidv7();
  }
  // Files go as raw bytes (packages/core-server/src/files.ts), everything else as JSON.
  const raw = options.body instanceof Blob ? options.body : undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = raw ? 'application/octet-stream' : 'application/json';
  }
  Object.assign(headers, options.headers);

  const timeout =
    options.timeout ?? (method === 'GET' && accept === 'application/json' ? READ_TIMEOUT_MS : 0);
  const response = await fetchWithin(
    `/api${path}`,
    {
      method,
      headers,
      credentials: 'same-origin',
      body: raw ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
      signal: options.signal,
    },
    timeout,
  );

  if (response.status === 401) for (const listener of listeners) listener();
  if (!response.ok) {
    let code: string | undefined;
    let message = response.statusText;
    let body: unknown;
    try {
      body = await response.json();
      const known = body as { message?: unknown; code?: unknown };
      if (typeof known.code === 'string') code = known.code;
      if (typeof known.message === 'string') message = known.message;
    } catch {
      // Body without JSON, keep the status text.
    }
    throw new ApiError(response.status, code, message, body);
  }
  return response;
}

async function request<T>(method: Method, path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(method, path, options, 'application/json');
  if (response.status === 204) return undefined as T;
  // 202 and 201 may come without a body, e.g. when a job was only queued.
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** The API of the kernel and the modules, always below `/api` on the same origin. */
export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body' | 'idempotencyKey'>) =>
    request<T>('GET', path, options),
  post: <T = void>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),
  put: <T = void>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, body }),
  patch: <T = void>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),
  delete: <T = void>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
  /** Raw content, e.g. of a file the device keeps for working offline. */
  blob: async (path: string, options?: Pick<RequestOptions, 'signal' | 'timeout'>) =>
    (await send('GET', path, options ?? {}, '*/*')).blob(),
};
